'use client'

import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'
import { AnimatePresence, motion } from 'motion/react'

interface VoiceVisualizerProps {
  isRecording: boolean
  className?: string
}

const BAR_COUNT = 32

const bars = Array.from({ length: BAR_COUNT }, (_, i) => i)

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0')
  const s = (seconds % 60).toString().padStart(2, '0')
  return `${m}:${s}`
}

function useElapsedTimer(isRecording: boolean) {
  const startRef = useRef(0)
  const elapsedRef = useRef(0)
  const listenersRef = useRef(new Set<() => void>())

  const subscribe = useCallback((cb: () => void) => {
    listenersRef.current.add(cb)
    return () => {
      listenersRef.current.delete(cb)
    }
  }, [])

  const getSnapshot = useCallback(() => elapsedRef.current, [])

  useEffect(() => {
    if (!isRecording) {
      elapsedRef.current = 0
      listenersRef.current.forEach((cb) => cb())
      return
    }
    startRef.current = Date.now()
    elapsedRef.current = 0
    listenersRef.current.forEach((cb) => cb())
    const id = setInterval(() => {
      elapsedRef.current = Math.floor((Date.now() - startRef.current) / 1000)
      listenersRef.current.forEach((cb) => cb())
    }, 1000)
    return () => clearInterval(id)
  }, [isRecording])

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

export function VoiceVisualizer({
  isRecording,
  className,
}: VoiceVisualizerProps) {
  const elapsed = useElapsedTimer(isRecording)

  return (
    <AnimatePresence>
      {isRecording && (
        <motion.div
          key="voice-visualizer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className={`flex h-14 w-full flex-col items-center justify-center gap-1 ${className ?? ''}`}
        >
          {/* Recording indicator row */}
          <div className="flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />
            <span className="text-muted-foreground font-mono text-xs tabular-nums">
              {formatTime(elapsed)}
            </span>
          </div>

          {/* Bars */}
          <div className="flex items-end gap-px">
            {bars.map((i) => {
              const minH = 4
              const maxH = 20
              const seed = ((i * 7) % 11) / 11
              const duration = 0.4 + seed * 0.6
              const delay = (i / BAR_COUNT) * 0.3

              return (
                <motion.span
                  key={i}
                  className="bg-foreground/40 inline-block w-0.5 rounded-full"
                  animate={{
                    height: [
                      `${minH + seed * (maxH - minH)}px`,
                      `${maxH - seed * (maxH - minH)}px`,
                      `${minH + seed * (maxH - minH)}px`,
                    ],
                  }}
                  transition={{
                    duration,
                    delay,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                  style={{ height: `${minH}px` }}
                />
              )
            })}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
